/* Form relay.

   A Shopify contact form can only email the store's own customer-service
   address, cannot be routed per form, and silently throws away any attached
   file. So every form posting to /contact is copied, as it is submitted, to
   the Apps Script (Customise > Footer > Integrations), which files the row in
   the right spreadsheet, saves the CV into Drive and emails info@ or hr@. The
   native Shopify submit still happens afterwards, so if the script is ever
   down nothing on the website breaks.

   THE CV FIELD IS ADDED HERE rather than in the careers section, deliberately:
   this file is the only thing that can actually deliver a file anywhere, so
   the control should not exist unless this is running.

   The scan repeats on a MutationObserver because the careers page builds its
   job panels after this script has run — a single pass at load found nothing.

   ==========================================================================
   THE GATE, AND WHY IT HAS TO BE HERE
   --------------------------------------------------------------------------
   Shopify loads an invisible captcha script onto every storefront form
   (ce_storefront_forms_captcha_hcaptcha). It listens on the same submit event
   we do, solves its challenge in the background, writes its token into the
   form and then calls form.submit() ITSELF. It never looks at
   event.defaultPrevented, so calling preventDefault here does not stop it.

   That is a race, and we were losing it. Measured on the live site: the
   captcha called submit() 403ms after the submit event, while our request to
   the Apps Script — which has to base64 the CV and upload it — took 3.8s with
   a one-byte test file, and far longer with a real CV. The page navigated
   away at 403ms and killed the request in flight. The applicant saw the
   confirmation, Shopify sent its notification, and nothing ever reached the
   spreadsheet or the Drive folder. A real application was lost that way on
   29 September and again on 1 October, and the two notifications Shopify sent
   each time were the captcha's submit and ours both going through.

   So form.submit is shadowed for the life of the form. While the gate is up a
   submit() call is remembered rather than performed; once our request has
   finished the gate drops and the submission goes through exactly once. The
   captcha's token is already in the form by the time it calls submit(), so
   holding that call back for a few seconds costs nothing.

   Do not "simplify" this back to preventDefault plus form.submit().
   ========================================================================== */

(function () {
  'use strict';

  var cfg = {};
  try {
    cfg = JSON.parse(document.getElementById('gs-relay-config').textContent);
  } catch (err) {
    return;
  }

  if (!cfg.endpoint) return;

  var MAX_MB = 10;
  var MAX = MAX_MB * 1024 * 1024;
  var seq = 0;
  var known = new WeakSet();

  /* Held before anything can shadow it, and always called with .call(form) —
     a form control named "submit" would otherwise hide the method. */
  var nativeSubmit = HTMLFormElement.prototype.submit;

  function label(input) {
    var n = input.getAttribute('name') || '';
    var m = n.match(/^contact\[(.+)\]$/);
    return (m ? m[1] : n).toLowerCase().replace(/[_\-]+/g, ' ').trim();
  }

  function isJobForm(form) {
    return Array.prototype.some.call(form.querySelectorAll('[name^="contact["]'), function (el) {
      return /applying|position|vacancy|job|role/.test(label(el));
    });
  }

  /* ------------------------------------------------------- the CV control */

  function addCv(form) {
    if (form.querySelector('input[type="file"]')) return;

    seq += 1;
    var id = 'gs-cv-' + seq;

    var box = document.createElement('div');
    box.className = 'gs-field gs-field--wide gs-cvfield';
    box.innerHTML =
      '<label for="' + id + '">CV or résumé <span aria-hidden="true">*</span></label>' +
      '<input id="' + id + '" type="file" name="cv" required ' +
      'accept=".pdf,.doc,.docx,.rtf,.odt,.txt,application/pdf,application/msword,' +
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document">' +
      '<small class="gs-cvfield__note">PDF or Word, up to ' + MAX_MB +
      ' MB. It goes straight to our HR folder.</small>';

    var actions = form.querySelector('.gs-apply__actions') ||
      (form.querySelector('[type="submit"]') || {}).parentElement;

    if (actions && actions.parentElement === form) form.insertBefore(box, actions);
    else form.appendChild(box);

    var input = box.querySelector('input');
    var note = box.querySelector('.gs-cvfield__note');
    var base = note.textContent;

    input.addEventListener('change', function () {
      var f = input.files && input.files[0];
      box.classList.remove('is-bad');

      if (!f) { note.textContent = base; input.setCustomValidity(''); return; }

      if (f.size > MAX) {
        /* Clear it as well as complaining — leaving an oversized file selected
           lets someone keep pressing Send against a form that cannot submit. */
        box.classList.add('is-bad');
        note.textContent = 'That file is ' + (f.size / 1048576).toFixed(1) +
          ' MB. The limit is ' + MAX_MB + ' MB — please choose a smaller one.';
        input.value = '';
        input.setCustomValidity('');
        return;
      }

      input.setCustomValidity('');
      note.textContent = f.name + ' — ' + Math.max(Math.round(f.size / 1024), 1) + ' KB, ready to send.';
    });
  }

  function readFile(file) {
    return new Promise(function (done) {
      if (!file || file.size > MAX) return done(null);

      var fr = new FileReader();
      fr.onload = function () {
        var s = String(fr.result);
        done({ name: file.name, type: file.type, data: s.slice(s.indexOf(',') + 1) });
      };
      fr.onerror = function () { done(null); };
      fr.readAsDataURL(file);
    });
  }

  /* The Shopify notification now says whether the real pipeline took it.
     Without this a silent relay failure looks exactly like a success from the
     inbox, which is how an application sat unnoticed for two days. */
  function stamp(form, text) {
    var field = form.querySelector('input[name="contact[Pipeline]"]');
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'contact[Pipeline]';
      form.appendChild(field);
    }
    field.value = text;
  }

  /* ------------------------------------------------------------ the relay */

  function wire(form) {
    if (known.has(form)) return;
    known.add(form);

    var job = isJobForm(form);
    if (job) addCv(form);

    var gate = false;   // while true, a submit() call is held rather than run
    var queued = false; // somebody (the captcha) asked to submit while held
    var posted = false; // the real submission has gone

    /* Shadowed for the life of the form. See the note at the top of the file. */
    form.submit = function () {
      if (gate) { queued = true; return; }
      if (posted) return;
      posted = true;
      nativeSubmit.call(form);
    };

    form.addEventListener('submit', function (event) {
      if (posted) return;

      var file = null;
      var bag = {};

      Array.prototype.forEach.call(form.querySelectorAll('input, textarea, select'), function (el) {
        if (el.type === 'file') {
          if (el.files && el.files[0]) file = el.files[0];
          return;
        }
        if (!el.name || el.name.indexOf('contact[') !== 0) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        bag[label(el)] = el.value;
      });

      var isJob = job || !!file;

      var payload = {
        secret: cfg.secret || '',
        kind: isJob ? 'career' : 'contact',
        page: window.location.pathname + window.location.search,
        name: bag.name || bag['full name'] || '',
        email: bag.email || '',
        phone: bag.phone || bag.telephone || '',
        company: bag.company || bag.organisation || '',
        message: bag.body || bag.message || bag.comments || '',
        role: bag['applying for'] || bag.position || bag.role || bag.job || '',
        jobRef: bag.reference || bag['job reference'] || ''
      };

      var extra = Object.keys(bag)
        .filter(function (k) {
          return !/^(name|full name|email|phone|telephone|company|organisation|body|message|comments|applying for|position|role|job|reference|job reference|country code|pipeline)$/.test(k);
        })
        .map(function (k) { return k + ': ' + bag[k]; });

      if (extra.length) payload.message += '\n\n' + extra.join('\n');

      event.preventDefault();
      gate = true;

      var released = false;

      /* Drop the gate and let the submission through. If the captcha already
         tried, go now — its token is in the form. If it has not finished yet,
         wait for it, with a backstop in case it never calls at all. */
      function release() {
        if (released) return;
        released = true;
        gate = false;
        window.clearTimeout(guard);

        if (queued) { form.submit(); return; }
        window.setTimeout(function () { form.submit(); }, 7000);
      }

      /* The endpoint being slow or dead must never cost somebody their
         application: after this, the form goes regardless. */
      var guard = window.setTimeout(function () {
        stamp(form, 'Relay timed out — this one is NOT in the sheet. Ask for the CV by email.');
        release();
      }, file ? 45000 : 8000);

      readFile(file)
        .then(function (cv) {
          if (cv) payload.cv = cv;
          return fetch(cfg.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify(payload)
          });
        })
        .then(function (res) { return res.text(); })
        .then(function (text) {
          var out = {};
          try { out = JSON.parse(text); } catch (err) { out = {}; }
          if (out && out.ok) {
            stamp(form, 'Filed as ' + (out.reference || 'a new row') +
              (out.cv ? ' — CV in Drive' : ''));
          } else {
            stamp(form, 'Relay refused this one — it is NOT in the sheet. Ask for the CV by email.');
          }
        })
        .catch(function () {
          stamp(form, 'Relay unreachable — this one is NOT in the sheet. Ask for the CV by email.');
        })
        .then(release);
    });
  }

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll('form[action*="/contact"]'), wire);
  }

  scan();

  if ('MutationObserver' in window) {
    var pending = null;
    new MutationObserver(function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(scan, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.setTimeout(scan, 1200);
  window.setTimeout(scan, 3000);
})();